﻿import React, { useMemo, useContext } from 'react';
import Context, { context_type } from '@Util/Context';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from "react-helmet";
import { useTranslation } from 'react-i18next';
import { Client_Website_Type } from '@Util/Types';
import dayjs from 'dayjs';
import Module_View_Toggler from '@Modules/Module_View_Toggler';
import { Icon_Arrow_Left } from '@Elements/ImageElements/Icons';

import { H1, H2, P } from '@Elements/Fonts';
import Content_Container from '@Elements/Content_Container';
import Exception_Count_Chart from '@Elements/Charts/ClientWebsites/Exception_Count_Chart';
import Client_Single_Website_Table from '@Elements/Client_Single_Website_Table';

export default function ClientWebsitespage(): JSX.Element {
    const [t] = useTranslation();
    const navigate = useNavigate();
    const { id } = useParams();
    const { client_websites }: context_type = useContext(Context);

    //const current_site: Client_Website_Type = useMemo(() => {
    //    return client_websites.find((site: Client_Website_Type) => site.id == id);
    //}, [id]);

    const current_site = useMemo(() => {
        if (!Array.isArray(client_websites) || client_websites.length === 0) return undefined;

        const found = client_websites.find((site: Client_Website_Type) => site.id == id);
        if (!found) return undefined;

        const reversedHistory = Array.isArray(found.history)
            ? [...found.history].reverse()
            : found.history;

        return {
            ...found,
            history: reversedHistory
        };
    }, [id, client_websites]);

    return (
        <>
            <button
                type="button"
                className="Button_Back"
                onClick={() => navigate(-1)}
            >
                <Icon_Arrow_Left />
                Tillbaka
            </button>
            <Helmet>
                <title>{`${current_site?.name} - InTime`}</title>
            </Helmet>
            <H1>{current_site?.name}</H1>
            <section className="Client_Site_Information">
                <Content_Container>
                    <H2 center={true}>{t('Text.LatestAmountOfExceptions')}</H2>
                    <P center={true}>{`${current_site?.lastExceptionCount.toFixed(1)} ${t('Text.Pcs')}`}</P>
                </Content_Container>
                <Content_Container>
                    <H2 center={true}>{t('Text.AverageAmountOfExceptions')}</H2>
                    <P center={true}>{`${current_site?.averageExceptionCount.toFixed(1)} ${t('Text.Pcs')}`}</P>
                </Content_Container>
                <Content_Container>
                    <H2 center={true}>{t('Text.LastResponseTime')}</H2>
                    <P center={true}>{`${current_site?.lastResponseTime.toFixed(1)} ms`}</P>
                </Content_Container>
                <Content_Container>
                    <H2 center={true}>{t('Text.AverageResponseTime')}</H2>
                    <P center={true}>{`${current_site?.averageResponseTime.toFixed(1)} ms`}</P>
                </Content_Container>
                <Content_Container>
                    <H2 center={true}>{t('Text.SSLExpirationDate')}</H2>
                    <P center={true}>{dayjs(current_site?.certificateExpirationDate).format("YYYY-MM-DD")}</P>
                </Content_Container>
            </section>
            <section className="Client_Site_Backtracing">
                <Module_View_Toggler
                    default_module={0}
                    module_one={<Exception_Count_Chart website_data={current_site?.history} />}
                    module_two={<Client_Single_Website_Table website_data={current_site?.history} />}
                    module_one_settings={{
                        icon: <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 -960 960 960" width="24"><path d="M280-280h80v-200h-80v200Zm320 0h80v-400h-80v400Zm-160 0h80v-120h-80v120Zm0-200h80v-80h-80v80ZM200-120q-33 0-56.5-23.5T120-200v-560q0-33 23.5-56.5T200-840h560q33 0 56.5 23.5T840-760v560q0 33-23.5 56.5T760-120H200Zm0-80h560v-560H200v560Zm0-560v560-560Z" /></svg>,
                        name: t('Text.Chart'),
                        module_identifier: 0
                    }}
                    module_two_settings={{
                        icon: <svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="list" className="svg-inline--fa fa-list fa-w-16" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#ffffff" d="M80 368H16a16 16 0 0 0-16 16v64a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16v-64a16 16 0 0 0-16-16zm0-320H16A16 16 0 0 0 0 64v64a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16V64a16 16 0 0 0-16-16zm0 160H16a16 16 0 0 0-16 16v64a16 16 0 0 0 16 16h64a16 16 0 0 0 16-16v-64a16 16 0 0 0-16-16zm416 176H176a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h320a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16zm0-320H176a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h320a16 16 0 0 0 16-16V80a16 16 0 0 0-16-16zm0 160H176a16 16 0 0 0-16 16v32a16 16 0 0 0 16 16h320a16 16 0 0 0 16-16v-32a16 16 0 0 0-16-16z"></path></svg>,
                        name: t('Text.List'),
                        module_identifier: 1
                    }}
                />
            </section>
        </>
    );
}